import React, { useState, useEffect } from 'react';
import { toast } from '../../../../utils/toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Truck, ArrowLeft, ArrowRight } from 'lucide-react';
import useAuth from '../../../../hooks/useAuth';
import { getActiveClientsApi } from '../../../../api/company';
import { getDeliveryUsers } from '../../../../api/user';
import useBillingStore from '../../../../store/billing';
import { Button } from '../../../../components/ui/Button';
import { Select } from '../../../../components/ui/Select';
import { Input } from '../../../../components/ui/Input';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { apiErrorMessage, apiErrorData, apiBodyMessages } from '../../../../utils/apiErrorMessage';

// Campos de fecha del DTO de generate → etiqueta visible del paso 2.
const PERIOD_LABELS = {
  periodStart: 'Inicio del periodo',
  periodEnd: 'Fin del periodo',
};

/**
 * 400 de validación de las fechas → «Inicio del periodo: …» / «Fin del periodo: …», unido
 * con ' · '. class-validator emite el campo como prefijo ('periodStart must be a valid ISO
 * 8601 date string'); el DTO solo tiene esas dos fechas, así que cualquier otro mensaje
 * (p.ej. 'companyId must be a mongodb id') se agrega crudo al final en vez de perderse.
 * Devuelve null si NINGÚN mensaje es de fecha: ahí se muestra el texto del backend entero
 * (las reglas de negocio ya vienen en español).
 * @param {unknown} err
 * @param {(key: string, opts?: object) => string} t
 * @returns {string|null}
 */
const describePeriodError = (err, t) => {
  const lines = [];
  const unmatched = new Set();
  const seen = new Set();
  apiBodyMessages(apiErrorData(err)).forEach((message) => {
    const route = message.split(' ')[0];
    if (!PERIOD_LABELS[route]) {
      unmatched.add(message);
      return;
    }
    if (seen.has(route)) return;
    seen.add(route);
    lines.push(`${t(PERIOD_LABELS[route])}: ${t('debe ser una fecha válida')}`);
  });
  if (!lines.length) return null;
  return lines.concat([...unmatched]).join(' · ');
};

// Selector de acciones (estable, no causa re-render innecesario)
const selectActions = (s) => ({
  saving: s.saving,
  generateClientPreDocument: s.generateClientPreDocument,
  generateDriverPreDocument: s.generateDriverPreDocument,
});

const TODAY = new Date().toISOString().split('T')[0];
const FIRST_OF_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  .toISOString()
  .split('T')[0];

const EMPTY_FORM = {
  docType: 'client_invoice',
  companyId: '',
  driverId: '',
  periodStart: FIRST_OF_MONTH,
  periodEnd: TODAY,
  notes: '',
};

const GeneratePreDocModal = ({ isOpen, toggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { saving, generateClientPreDocument, generateDriverPreDocument } = useBillingStore(selectActions);

  const logisticCompanyId = user?.logisticCompany?._id ?? null;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);

  // Data
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Load clients and drivers when modal opens
  useEffect(() => {
    if (!isOpen || !logisticCompanyId) return;

    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const data = await getActiveClientsApi(logisticCompanyId);
        if (Array.isArray(data)) {
          setClients(data.map((c) => ({ value: c._id, label: c.name || t('Sin nombre') })));
        }
      } catch {
        toast.error(t('No se pudieron cargar los clientes'));
      } finally {
        setLoadingClients(false);
      }
    };

    const fetchDrivers = async () => {
      setLoadingDrivers(true);
      try {
        const { docs } = await getDeliveryUsers('active', 'delivery', logisticCompanyId);
        if (Array.isArray(docs)) {
          setDrivers(
            docs.map((d) => ({
              value: d._id,
              label: `${d.name ?? ''} ${d.lastName ?? ''}`.trim() || t('Sin nombre'),
            })),
          );
        }
      } catch {
        toast.error(t('No se pudieron cargar los repartidores'));
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchClients();
    fetchDrivers();
  }, [isOpen, logisticCompanyId]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleClose = () => {
    setStep(1);
    setForm(EMPTY_FORM);
    toggle();
  };

  const validateStep1 = () => {
    if (form.docType === 'client_invoice' && !form.companyId) {
      toast.error(t('Selecciona un cliente para generar el pre-documento'));
      return false;
    }
    if (form.docType === 'driver_payment' && !form.driverId) {
      toast.error(t('Selecciona un repartidor para generar el pre-documento'));
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    // El DTO exige @IsDateString en las dos fechas del paso 2. Sin esto el 400 llegaría
    // como array en inglés ('periodStart must be a valid ISO 8601 date string').
    if (!form.periodStart) {
      toast.error(t('Completa {{campo}}.', { campo: t('Inicio del periodo') }));
      return;
    }
    if (!form.periodEnd) {
      toast.error(t('Completa {{campo}}.', { campo: t('Fin del periodo') }));
      return;
    }
    try {
      let result;
      if (form.docType === 'client_invoice') {
        result = await generateClientPreDocument({
          companyId: form.companyId,
          periodStart: form.periodStart,
          periodEnd: form.periodEnd,
          notes: form.notes || undefined,
        });
      } else {
        result = await generateDriverPreDocument({
          driverId: form.driverId,
          periodStart: form.periodStart,
          periodEnd: form.periodEnd,
          notes: form.notes || undefined,
        });
      }
      toast.success(t('Pre-documento generado correctamente'));
      handleClose();
      navigate(`/admin/billing/pre-documents/${result._id}`);
    } catch (err) {
      toast.error(
        describePeriodError(err, t) ?? apiErrorMessage(err, t('Error al generar pre-documento')),
      );
    }
  };

  // Label of currently selected client/driver for the summary
  const selectedClientLabel =
    form.companyId
      ? (clients.find((c) => c.value === form.companyId)?.label ?? form.companyId)
      : '—';
  const selectedDriverLabel =
    form.driverId
      ? (drivers.find((d) => d.value === form.driverId)?.label ?? form.driverId)
      : '—';

  const DOC_TYPES = [
    { value: 'client_invoice', label: t('Factura a Cliente'), Icon: Building2 },
    { value: 'driver_payment', label: t('Pago a Repartidor'), Icon: Truck },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={`${t('Generar Pre-documento')} — ${t('Paso')} ${step} ${t('de')} 2`}
      footer={
        step === 1 ? (
          <>
            <Button variant="outline" onClick={handleClose}>{t('Cancelar')}</Button>
            <Button
              onClick={() => { if (validateStep1()) setStep(2); }}
              iconRight={<ArrowRight className="w-4 h-4" />}
            >
              {t('Siguiente')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => setStep(1)} iconLeft={<ArrowLeft className="w-4 h-4" />}>
              {t('Atrás')}
            </Button>
            <Button onClick={handleGenerate} loading={saving}>
              {t('Generar Pre-documento')}
            </Button>
          </>
        )
      }
    >
      {/* ── STEP 1: tipo + entidad ── */}
      {step === 1 && (
        <>
          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Tipo de documento')}</label>
            <div className="bill-radio-group">
              {DOC_TYPES.map(({ value, label, Icon }) => (
                <label
                  key={value}
                  className={`bill-radio${form.docType === value ? ' bill-radio--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="docType"
                    value={value}
                    checked={form.docType === value}
                    onChange={handleChange}
                  />
                  <Icon className="w-[18px] h-[18px]" aria-hidden />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Cliente selector */}
          {form.docType === 'client_invoice' && (
            <div className="bill-field" style={{ marginBottom: '1rem' }}>
              <label className="bill-label">
                {t('Cliente')} <span className="text-status-error">*</span>
              </label>
              {loadingClients ? (
                <Skeleton className="h-10 w-full rounded-lg" />
              ) : (
                <Select name="companyId" value={form.companyId} onChange={handleChange}>
                  <option value="">— {t('Todos')} —</option>
                  {clients.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              )}
              {clients.length === 0 && !loadingClients && (
                <small className="bill-hint bill-hint--accent">
                  {t('No se encontraron clientes con contratos activos')}
                </small>
              )}
            </div>
          )}

          {/* Repartidor selector */}
          {form.docType === 'driver_payment' && (
            <div className="bill-field" style={{ marginBottom: '1rem' }}>
              <label className="bill-label">
                {t('Repartidor')} <span className="text-status-error">*</span>
              </label>
              {loadingDrivers ? (
                <Skeleton className="h-10 w-full rounded-lg" />
              ) : (
                <Select name="driverId" value={form.driverId} onChange={handleChange}>
                  <option value="">— {t('Todos')} —</option>
                  {drivers.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </Select>
              )}
              {drivers.length === 0 && !loadingDrivers && (
                <small className="bill-hint bill-hint--accent">
                  {t('No se encontraron repartidores activos')}
                </small>
              )}
            </div>
          )}
        </>
      )}

      {/* ── STEP 2: periodo + resumen ── */}
      {step === 2 && (
        <>
          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Inicio del periodo')}</label>
            <Input type="date" name="periodStart" value={form.periodStart} onChange={handleChange} max={form.periodEnd} />
          </div>

          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Fin del periodo')}</label>
            <Input type="date" name="periodEnd" value={form.periodEnd} onChange={handleChange} min={form.periodStart} />
          </div>

          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Notas internas (opcional)')}</label>
            <textarea
              className="bill-textarea"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={2}
              placeholder={t('Ej: Periodo de cierre enero, ajuste manual aplicado...')}
            />
          </div>

          {/* Resumen */}
          <div className="bill-recap">
            <div className="bill-recap__title">{t('Resumen')}</div>
            <div className="bill-recap__grid">
              <span className="bill-recap__k">{t('Tipo')}:</span>
              <span className="bill-recap__v">
                {form.docType === 'client_invoice' ? t('Factura a Cliente') : t('Pago a Repartidor')}
              </span>
              {form.docType === 'client_invoice' && (
                <>
                  <span className="bill-recap__k">{t('Cliente')}:</span>
                  <span className="bill-recap__v">{selectedClientLabel}</span>
                </>
              )}
              {form.docType === 'driver_payment' && (
                <>
                  <span className="bill-recap__k">{t('Repartidor')}:</span>
                  <span className="bill-recap__v">{selectedDriverLabel}</span>
                </>
              )}
              <span className="bill-recap__k">{t('Periodo')}:</span>
              <span className="bill-recap__v">{form.periodStart} → {form.periodEnd}</span>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default GeneratePreDocModal;
